import { PeopleForm } from "Bands/components";
import { BAND_PATHS } from "Bands/router";
import { addMembersToPeopleBlock, clearActionMeta, dataMetaSelector } from "Bands/state";
import {
  BAND_ACTIONS,
  IPeopleBlockParams,
  IPeopleBlockFormData,
  IPeopleBlockUser,
  PeopleBlockStatus,
} from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { copy } from "shared/assets";
import { Card, Title } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import { IAPIState, ICardTheme, IFormButtons, IStatus } from "shared/types";

const peopleBlockCopy = copy.peopleBlock.edit;

interface AddPeopleFormProps {
  blockId: string;
  users?: IPeopleBlockUser[];
}

export const AddPeopleForm: FC<AddPeopleFormProps> = ({ blockId, users }) => {
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.addMembersToPeopleBlock));

  useClearActionMetaOnUnMount(BAND_ACTIONS.addMembersToPeopleBlock);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.addMembersToPeopleBlock));

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: peopleBlockCopy.messages.added,
        })
      );
    }
  }, [dispatch, apiMeta?.status]);

  const handleSubmit = ({ users, status }: IPeopleBlockFormData) => {
    const cleanData: IPeopleBlockParams = {
      users: users.map(({ value }) => API_ENDPOINTS.BAND_USER(value)),
      status,
      rsvpUrl: `${window.location.origin}${BAND_PATHS.ANSWER_RSVP}?token=[TOKEN]&status=[STATUS]`,
    };

    dispatch(
      addMembersToPeopleBlock({
        id: blockId,
        data: cleanData,
      })
    );
  };

  const [selectedStatus, setSelectedStatus] = useState<PeopleBlockStatus | null | undefined>(null);

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        children:
          selectedStatus === PeopleBlockStatus.rsvp
            ? peopleBlockCopy.buttons.addRSVP
            : peopleBlockCopy.buttons.add,
        size: "m",
        "data-testid": "add_people",
      },
    }),
    [selectedStatus]
  );

  return (
    <Card theme={ICardTheme.secondary} size="m">
      <Spacer size="m">
        <Title asComponent="h3" size="m">
          {peopleBlockCopy.addMember}
        </Title>
        <PeopleForm
          handleSubmit={handleSubmit}
          buttons={buttons}
          apiStatus={apiMeta?.status}
          error={!!apiMeta?.error}
          resetForm={apiMeta?.status === IAPIState.succeeded}
          onStatusChange={status => setSelectedStatus(status)}
          excludeOptions={users?.map(({ user }) => user.id)} // we need the BandUser id
          apiError={apiMeta?.error}
        />
      </Spacer>
    </Card>
  );
};
